import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../utils/api';

interface AuthContextType {
  isAuthenticated: boolean;
  clientId: string | null;
  email: string;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  loading: boolean;
  error: string | null;
  token: string;
  emailSubmitted: boolean;
  setEmailSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
  passkeyAvailable: boolean;
  loginWithEmail: (email: string, turnstileToken: string) => Promise<{ success: boolean; message: string }>;
  loginWithPasskey: (email: string) => Promise<{ success: boolean; message: string }>;
  verifyToken: (token: string) => Promise<{ success: boolean; message: string }>;
  resendToken: () => Promise<{ success: boolean; message: string }>;
  logout: () => void;
  reloadAuthStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [clientId, setClientId] = useState<string | null>(null);
  const [email, setEmail] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string>('');
  const [emailSubmitted, setEmailSubmitted] = useState<boolean>(false);
  const [passkeyAvailable, setPasskeyAvailable] = useState<boolean>(false);

  useEffect(() => {
    import('../services/PasskeyService').then(({ PasskeyService }) => {
      setPasskeyAvailable(PasskeyService.isAvailable());
    });
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await api.get('/api/auth/status');
      if (response.success) {
        setIsAuthenticated(true);
        setClientId(response.clientId);
      } else {
        setIsAuthenticated(false);
        setClientId(null);
      }
    } catch {
      setIsAuthenticated(false);
      setClientId(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const loginWithEmail = async (email: string, turnstileToken: string): Promise<{ success: boolean; message: string }> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/api/auth/login', { email, turnstileToken });
      if (response.success) {
        setEmail(email);
        setEmailSubmitted(true);
        return { success: true, message: response.message || 'Check your email for the verification token' };
      } else {
        setError(response.message || 'Email not found');
        return { success: false, message: response.message || 'Email not found' };
      }
    } catch {
      const errorMessage = 'An error occurred during login';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const verifyToken = async (token: string): Promise<{ success: boolean; message: string }> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/api/auth/verify', { email, token });
      if (response.success) {
        setIsAuthenticated(true);
        setClientId(response.clientId);
        setToken('');
        setEmailSubmitted(false);
        return { success: true, message: response.message || 'Successfully authenticated' };
      } else {
        setError(response.message || 'Invalid token');
        return { success: false, message: response.message || 'Invalid token' };
      }
    } catch {
      const errorMessage = 'An error occurred during verification';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const resendToken = async (): Promise<{ success: boolean; message: string }> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/api/auth/resend', { email });
      if (response.success) {
        return { success: true, message: response.message || 'Token resent successfully' };
      } else {
        setError(response.message || 'Failed to resend token');
        return { success: false, message: response.message || 'Failed to resend token' };
      }
    } catch {
      const errorMessage = 'An error occurred when resending token';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const loginWithPasskey = async (email: string): Promise<{ success: boolean; message: string }> => {
    setLoading(true);
    setError(null);
    try {
      const { PasskeyService } = await import('../services/PasskeyService');
      const result = await PasskeyService.authenticateWithPasskey(email);

      if (result.success) {
        const response = await api.get('/api/auth/status');
        if (response.success) {
          setIsAuthenticated(true);
          setClientId(response.clientId);
        }
      } else {
        setError(result.message);
      }

      return result;
    } catch {
      const errorMessage = 'An error occurred during passkey authentication';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    api.post('/api/auth/logout', {})
      .then(() => {
        setIsAuthenticated(false);
        setClientId(null);
        setEmail('');
        setEmailSubmitted(false);
        window.location.href = '/login';
      })
      .catch(() => {
        setIsAuthenticated(false);
        setClientId(null);
        setEmail('');
        setEmailSubmitted(false);
      });
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        clientId,
        email,
        setEmail,
        loading,
        error,
        token,
        emailSubmitted,
        setEmailSubmitted,
        passkeyAvailable,
        loginWithEmail,
        loginWithPasskey,
        verifyToken,
        resendToken,
        logout,
        reloadAuthStatus: checkAuthStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
